import { evictCacheByKeys } from 'helpers';
import { MutationHookOptions, useMutation } from '@apollo/client';
import { UPDATE_PACK_FAVORITED_STATE } from 'apollo/pack/pack.gql';
import { UPDATE_REPACK_FAVORITED_STATE } from 'apollo/repack/repack.gql';
import { UPDATE_SAMPLE_FAVORITED_STATE } from 'apollo/sample';
import { FavoriteItem } from 'types';
import { PackType } from 'types/enums';
import { useNotificationsContext } from 'contexts/NotificationsContext/NotificationsContext';
import FavoritesPageQueryConfig from 'apollo/favorites/favorites';
import {
    AddedToFavoritesError,
    AddedToFavoritesSuccess,
    RemovedFromFavoritesError,
    RemovedFromFavoritesSuccess,
} from 'contexts/NotificationsContext/notifications';

const getMutationConfig = (
    item: FavoriteItem,
    handleErrorNotifications: (item: FavoriteItem) => void,
    handleSuccessNotifications: (item: FavoriteItem) => void,
): MutationHookOptions => {
    const updatePayload = {
        guid: item.guid,
        // info: updatePayload.isFavorited is the item current value
        isFavorited: item.isFavorited,
    };

    switch (item.type) {
        case PackType.Pack:
            return {
                variables: {
                    pack: updatePayload,
                },
                update: (cache, { data: { updatePackFavoritedState } }) => {
                    if (updatePackFavoritedState.guid) {
                        handleSuccessNotifications(item);

                        cache.modify({
                            id: cache.identify(updatePackFavoritedState),
                            fields: {
                                isFavorited() {
                                    return !updatePayload.isFavorited;
                                },
                            },
                        });

                        evictCacheByKeys(cache, [
                            FavoritesPageQueryConfig.packs.pathToItems,
                        ]);
                    }
                },
                onQueryUpdated(observableQuery) {
                    return observableQuery.refetch();
                },
                onError: () => {
                    handleErrorNotifications(item);
                },
            };
        case PackType.RePack:
            return {
                variables: {
                    repack: updatePayload,
                },
                update: (cache, { data: { updateRepackFavoritedState } }) => {
                    if (updateRepackFavoritedState.guid) {
                        handleSuccessNotifications(item);
                        cache.modify({
                            id: cache.identify(updateRepackFavoritedState),
                            fields: {
                                isFavorited() {
                                    return !updatePayload.isFavorited;
                                },
                            },
                        });
                        evictCacheByKeys(cache, [
                            FavoritesPageQueryConfig.packs.pathToItems,
                        ]);
                    }
                },
                onQueryUpdated(observableQuery) {
                    return observableQuery.refetch();
                },
                onError: () => {
                    handleErrorNotifications(item);
                },
            };
        default:
            return {
                variables: {
                    sample: updatePayload,
                },
                update: (cache, { data: { updateSampleFavoritedState } }) => {
                    if (updateSampleFavoritedState.guid) {
                        handleSuccessNotifications(item);

                        cache.modify({
                            id: cache.identify(updateSampleFavoritedState),
                            fields: {
                                isFavorited() {
                                    return !updatePayload.isFavorited;
                                },
                            },
                        });

                        evictCacheByKeys(cache, [
                            FavoritesPageQueryConfig.samples.pathToItems,
                        ]);
                    }
                },
                onQueryUpdated(observableQuery) {
                    return observableQuery.refetch();
                },
                onError: () => {
                    handleErrorNotifications(item);
                },
            };
    }
};

export type UpdateFavoriteItemType = (
    item: FavoriteItem,
    handleNotAuthenticated?: () => void,
) => void;

export const useUpdateItemFavoriteStatus = (): {
    updateFavorite: UpdateFavoriteItemType;
} => {
    const [updatePack] = useMutation(UPDATE_PACK_FAVORITED_STATE);
    const [updateRepack] = useMutation(UPDATE_REPACK_FAVORITED_STATE);
    const [updateSample] = useMutation(UPDATE_SAMPLE_FAVORITED_STATE);

    const { createNotification } = useNotificationsContext();

    const handleErrorMessage = (item: FavoriteItem) => {
        if (item.isFavorited) {
            createNotification(new RemovedFromFavoritesError(item.name));
        } else {
            createNotification(new AddedToFavoritesError(item.name));
        }
    };

    const handleSuccessMessage = (item: FavoriteItem) => {
        if (item.isFavorited) {
            createNotification(new RemovedFromFavoritesSuccess(item.name));
        } else {
            createNotification(new AddedToFavoritesSuccess(item.name));
        }
    };

    const updateFavorite: UpdateFavoriteItemType = (item) => {
        const options = getMutationConfig(
            item,
            handleErrorMessage,
            handleSuccessMessage,
        );

        switch (item.type) {
            case PackType.Pack:
                updatePack(options);
                break;
            case PackType.RePack:
                updateRepack(options);
                break;
            default:
                updateSample(options);
                break;
        }
    };

    return {
        updateFavorite,
    };
};
